import {
	type PropsWithChildren,
	createContext,
	type Dispatch,
	type FunctionComponent,
	type SetStateAction,
	useState,
	useMemo,
	useCallback
} from 'react';
import { type PricedOptionGroupFieldsFragment, type PricedOptionSelectionFieldsFragment } from '../../__generated__/graphql-client-types';
import type { ProductSubItem } from '../../types/product.types';

export type ProductPricedOptionsContextState = {
	selectedOptions: (PricedOptionSelectionFieldsFragment | null)[];
	textInputOptions: PricedOptionSelectionFieldsFragment[];
	handleSetTextInput: (option: PricedOptionSelectionFieldsFragment, value: string) => void;
	atcLoading: boolean;
	setAtcLoading: Dispatch<SetStateAction<boolean>>;
	configurationValid: boolean;
	handleSetOption: (option: PricedOptionSelectionFieldsFragment | null, index: number) => void;
	subItems: ProductSubItem[];
	setSubItems: Dispatch<SetStateAction<ProductSubItem[]>>;
	mainItemQuantity: number;
	setMainItemQuantity: Dispatch<SetStateAction<number>>;
};

export const pricedOptionsContextInitialState: ProductPricedOptionsContextState = {
	selectedOptions: [],
	textInputOptions: [],
	handleSetTextInput: () => {},
	atcLoading: false,
	setAtcLoading: () => {},
	configurationValid: false,
	handleSetOption: () => {},
	subItems: [],
	setSubItems: () => {},
	mainItemQuantity: 0,
	setMainItemQuantity: () => {}
};

export const ProductPricedOptionsContext = createContext<ProductPricedOptionsContextState>({ ...pricedOptionsContextInitialState });

export const ProductPricedOptionsContextProvider: FunctionComponent<
	PropsWithChildren<{ productOptionsGroups: PricedOptionGroupFieldsFragment[] }>
> = ({ children, productOptionsGroups }) => {
	const [selectedOptions, setSelectedOptions] = useState<(PricedOptionSelectionFieldsFragment | null)[]>(() =>
		productOptionsGroups.map((group) => group.pricedOptions.find((option) => option.isDefaultSelection) || null)
	);
	const [textInputOptions, setTextInputOptions] = useState<PricedOptionSelectionFieldsFragment[]>([]);
	const [atcLoading, setAtcLoading] = useState(false);
	const [subItems, setSubItems] = useState<ProductSubItem[]>([]);
	const [mainItemQuantity, setMainItemQuantity] = useState(1);

	// Uses the functional updater so batched calls compose instead of overwriting each other,
	// and so the callback identity stays stable across selection changes
	const handleSetOption = useCallback((option: PricedOptionSelectionFieldsFragment | null, index: number) => {
		setSelectedOptions((prev) => {
			const newSelectedOptions = [...prev];
			newSelectedOptions[index] = option;
			return newSelectedOptions;
		});
	}, []);

	// Store text input values keyed by option id: replace an existing entry, and drop it entirely when cleared
	const handleSetTextInput = useCallback((option: PricedOptionSelectionFieldsFragment, value: string) => {
		setTextInputOptions((prev) => {
			const withoutOption = prev.filter((textOption) => textOption.id !== option.id);
			return value ? [...withoutOption, { ...option, value }] : withoutOption;
		});
	}, []);

	const isValidKeyCode = (value: string): boolean => /^\d{5}$/.test(value);

	// Cart API rejects the configuration unless every required group has a selection and every provided text input is valid
	const configurationValid = useMemo(() => {
		const requiredGroupsSatisfied = productOptionsGroups
			.flatMap((group, index) => (group.required ? [index] : []))
			.every((index) => selectedOptions[index] !== null);
		const textInputsValid = textInputOptions.every((option) => isValidKeyCode(option.value));
		return requiredGroupsSatisfied && textInputsValid;
	}, [productOptionsGroups, selectedOptions, textInputOptions]);

	const productPricedOptionsContextValue = useMemo(
		() => ({
			selectedOptions,
			textInputOptions,
			handleSetTextInput,
			atcLoading,
			setAtcLoading,
			configurationValid,
			handleSetOption,
			subItems,
			setSubItems,
			mainItemQuantity,
			setMainItemQuantity
		}),
		[selectedOptions, textInputOptions, handleSetTextInput, atcLoading, configurationValid, handleSetOption, subItems, mainItemQuantity]
	);
	return <ProductPricedOptionsContext.Provider value={productPricedOptionsContextValue}>{children}</ProductPricedOptionsContext.Provider>;
};
